import type { LocaleMessages } from './en'

export const es: LocaleMessages = {
	setupWizard: {
		// Steps
		STEP_1_TITLE: 'Comenzar',
		STEP_1_SUBTITLE: 'Configura los aspectos básicos de tu diseño',
		STEP_2_TITLE: 'Elige el logo de tu empresa',
		STEP_2_SUBTITLE:
			'Elige un logo para que aparezca en el encabezado y/o pie de página',
		STEP_3_TITLE: 'Convierte productos en muestras virtuales',
		STEP_3_SUBTITLE:
			'Elige un logo para aplicar a los productos que agregues a tu diseño',

		// Form fields
		ADD_NAME: 'Dale un nombre a tu diseño',
		SELECT_CURRENCY: 'Elige una moneda',
		USE_AUTO_LOGO: 'Agregar automáticamente el logo a tu diseño',
		SELECT_LOGO: 'Seleccionar logo',
		UNTITLED: 'Sin título',
		SELECT_YOUR_DESIGN_TYPE: 'Orientación',

		// Orientation
		PORTRAIT: 'Vertical',
		LANDSCAPE: 'Horizontal',

		// Buttons
		BACK: 'Atrás',
		CONTINUE: 'Continuar',
		SKIP: 'Saltar',
		STEP_OF: 'Paso {current} de {total}',

		// Messages
		NAME_IS_REQUIRED:
			'El campo nombre es obligatorio y debe tener menos de 64 caracteres',
		UNABLE_TO_SAVE_DESIGN_SETTINGS:
			'No se pudo guardar la configuración del diseño, por favor intenta de nuevo',
	},

	pagination: {
		ADD_PAGE: 'Agregar una página',
		SINGLE_PAGE: 'Página simple',
		SPREAD_PAGE: 'Páginas enfrentadas',
		DELETE_PAGE: (data: Record<string, any>) =>
			(data.values.count ?? 0) > 1
				? `Eliminar ${data.values.count} páginas`
				: 'Eliminar página',
		DUPLICATE_PAGE: 'Duplicar página',
		DELETE_CONFIRMATION: (data: Record<string, any>) =>
			(data.values.count ?? 0) > 1
				? `¿Estás seguro de que quieres eliminar las ${data.values.count} páginas seleccionadas?`
				: '¿Estás seguro de que quieres eliminar esta página?',
		YOU_CANNOT_SELECT_MORE_THAN: (data: Record<string, any>) =>
			`No puedes seleccionar más de ${data.values.count} páginas a la vez`,
	},

	startScreen: {
		// Header
		TITLE: 'Comienza',
		SUBTITLE:
			'Crea nuevo, comienza desde una plantilla o abre un proyecto existente',
		NEW_DESIGN: 'Crear un nuevo diseño',

		// Sections
		MY_DESIGNS: 'Mis Diseños',
		TEMPLATES: 'Plantillas',
		TEMPLATES_SS: 'Plantillas S&S',
		BROWSE_DESIGNS: 'Explorar mis diseños',
		CONTINUE_WORKING: 'Continúa trabajando en tus diseños',

		// Design actions
		CREATE: 'Crear',
		EDIT: 'Editar',
		USE_TEMPLATE: 'Usar plantilla',

		// Design card
		CREATE_NEW_DESIGN: 'Crear un nuevo diseño',

		// Options menu
		VIEW_DESIGN: 'Ver diseño',
		DUPLICATE_DESIGN: 'Duplicar diseño',
		DELETE_DESIGN: 'Eliminar diseño',

		// Delete confirmation
		DESIGN: 'Diseño',
		DELETE_CONFIRMATION: '¿Estás seguro de que quieres eliminar el diseño',
		DELETE_YES: 'Sí, eliminar diseño',
		CANCEL: 'Cancelar',
		DELETE_SUCCESS: 'El diseño fue eliminado exitosamente',

		// Loading
		LOADING_MORE: 'Cargando más',
		NO_MORE_RESULTS: 'No hay más resultados',
	},

	setupWizardCompleted: {
		TITLE: '¡Todo listo! Comienza tu diseño ahora.',
		DESCRIPTION:
			'Puedes editar estas configuraciones en cualquier momento haciendo clic en',
		DESIGN_SETTINGS: 'Configuración del Diseño',
		CONTINUE_TO_DESIGN: 'Continuar a tu diseño',
		CONTINUE_TO_ADD_PRODUCTS: 'Continuar para agregar productos',
		PROCESSING: 'Procesando...',
	},

	toolbar: {
		// Publish
		SHARE: 'Compartir',
		PUBLISH_DESIGN: 'Publicar diseño',
		PUBLISH_CHANGES: 'Publicar cambios',
		DESIGN_PRIVATE: 'Este diseño es privado.',
		SHARE_YOUR_DESIGN_INSTANTLY: '¡Comparte tu diseño al instante!',
		SHARE_YOUR_DESIGN_INSTANTLY_DESCRIPTION:
			'¡Publica tu diseño para obtener un enlace compartible para correos electrónicos, sitios web, redes sociales y más!',

		// Design Settings
		DESIGN_SETTINGS: 'Configuración del diseño',
		DRAFT_SAVED: 'Borrador guardado',
		SAVING: 'Guardando...',
		MANAGE_YOUR_DESIGN_SETTINGS: 'Administra la configuración de tu diseño',
		UPDATE_YOUR_DESIGN_NAME_AND_LOGO:
			'Actualiza el nombre y logo de tu diseño, administra la configuración de muestras virtuales y más.',

		// Manage Page
		MANAGE_PAGE: 'Administrar página',
		LEFT_PAGE: 'Página izquierda',
		RIGHT_PAGE: 'Página derecha',
		REORDER_PAGES: 'Reordenar páginas',
		IS_TEMPLATE: 'Este diseño es una plantilla',
		TEMPLATE_TEXT:
			'Comparte este diseño como plantilla para permitir que otros lo dupliquen y personalicen como propio.',
		WATCH_TUTORIAL: 'Ver tutorial',
		CHANGE_THE_PAGE_LAYOUT: 'Cambiar el diseño de la página',
		CHANGE_THE_PAGE_LAYOUT_DESCRIPTION:
			'¿Página de portada? ¿Dos productos y un banner? ¿Un solo producto destacado? ¡Elige el diseño de página perfecto aquí!',

		// Errors
		INVALID_PROJECT:
			'Faltan detalles del proyecto. Por favor intenta de nuevo o contacta soporte.',
	},

	pageSidebar: {
		MANAGE_PAGE: 'Administrar Página',
		SELECT_LAYOUT_TITLE: 'Seleccionar diseño de página',
		PAGE_SETTINGS_TITLE: 'Información del producto en la página',
		MANAGE_PAGE_CONTENT: 'Administrar contenido de la página',
		SIDEBAR_SETTINGS_TITLE: 'Información del producto en la barra lateral',
		CHANGE_LAYOUT_BTN: 'Cambiar diseño',
		LAYOUT_NAMES: {
			BASIC_FOUR: '4 Productos Básicos',
		},
	},

	designSettings: {
		DESIGN_SETTINGS: 'Configuración del Diseño',
		GENERAL: 'General',
		TEMPLATE: 'Plantilla',
		VIRTUALS: 'Muestras virtuales',
		DATA: 'Datos',
		CREATE_DESIGN: 'Crear diseño',
		SAVE_SETTINGS: 'Guardar Configuración',
		NAME_IS_REQUIRED:
			'El campo nombre es obligatorio y debe tener menos de 64 caracteres',
		UNABLE_TO_SAVE_DESIGN_SETTINGS:
			'No se pudo guardar la configuración del diseño, por favor intenta de nuevo',
		PAGE_VISIBILITY: 'Visibilidad en página',
		SIDEBAR_VISIBILITY: 'Visibilidad en barra lateral',
		SELECT_DATA_TO_INCLUDE: 'Seleccione los datos que desea incluir',
		PAGE: 'Página',
		SIDEBAR: 'Barra lateral',

		// General Tab
		ADD_NAME: 'Agregue un nombre a su diseño',
		SELECT_CURRENCY: 'Seleccione la moneda para este diseño',
		USE_AUTO_LOGO: 'Agregar automáticamente el logo a su diseño',
		SELECT_LOGO: 'Seleccione su logo',
		UNTITLED: 'Sin título',
		SELECT_YOUR_DESIGN_TYPE: 'Seleccione su tipo de diseño',
		PORTRAIT: 'Vertical',
		LANDSCAPE: 'Horizontal',

		// Data Tab
		MARK_UP_DOWN_PRICES: 'Margen/descuento de sus precios',
		DISCOUNT: 'Descuento',
		MARK_UP: 'Margen',
		OVERRIDE_SELECTED_DATA: 'Seleccione los datos que desea incluir en su diseño',
	},

	userMenu: {
		NEW: 'Crear Nuevo Diseño',
		MY_DESIGNS: 'Mis Diseños',
		DASHBOARD: 'Panel de Control',
		SIGN_OUT: 'Cerrar Sesión',
	},

	layoutPicker: {
		SELECT_LAYOUT: 'Seleccione un diseño de página',
		LAYOUT_CATEGORIES: 'Categorías de diseño',
		ALL_LAYOUTS: 'Todos los diseños',
		COVER_PAGES: 'Páginas de portada',
		BASIC_LAYOUTS: 'Diseños básicos',
		GRAPHIC_LAYOUTS: 'Diseños gráficos',
		VIRTUALS_LAYOUTS: 'Diseños virtuales',
		TEXT_LAYOUTS: 'Diseños de texto',

		// Help section
		NEED_NEW_TEMPLATE: '¿Necesitas una nueva plantilla?',
		TEMPLATE_LIBRARY_GROWING:
			'¡La biblioteca de plantillas está creciendo rápido! Dinos qué estás buscando.',
		SEND_SUGGESTION: 'Enviar una sugerencia',

		// Empty state
		MORE_TEMPLATES_COMING: 'Próximamente más plantillas',
		ALWAYS_ADDING_NEW:
			'Siempre estamos agregando nuevas plantillas. Vuelve pronto.',

		// Layout actions
		USE_LAYOUT: 'Usar diseño',
		SELECTED: 'Seleccionado',
		NOT_AVAILABLE: 'No disponible',
		COMING_SOON: 'Próximamente',
		LAYOUT_NOT_AVAILABLE_TEXT:
			'Has agregado más productos al lienzo de los que este diseño admite. Para usarlo, elimina algunos productos.',
	},

	product: {
		ALL_COLORS: 'Todos los Colores',
	},

	publishModal: {
		PUBLISH_TEMPLATE: 'Publicar plantilla',
		PUBLISH_TEMPLATE_CHANGES: 'Publicar cambios de plantilla',
		PUBLISH_TEMPLATE_TITLE: '¿Estás listo para publicar tu plantilla?',
		PUBLISH_TEMPLATE_TEXT:
			'Una vez que hayas publicado tu diseño como plantilla, puedes compartirlo con otras personas para que puedan personalizarlo fácilmente como propio.',
		PUBLISH_CHANGES: 'Publicar cambios',
		PUBLISH_CHANGES_TITLE:
			'¿Estás seguro de que quieres publicar estos cambios en tu plantilla?',
		PUBLISH_CHANGES_TEXT:
			'Estos cambios se harán públicos para aquellos que tengan acceso a tu diseño en el visor. Esto no se puede deshacer.',
		PUBLISH_DESIGN:
			'¿Estás listo para publicar tu diseño y comenzar a compartir?',
		YES_PUBLISH: 'Sí, publicar',
		CANCEL: 'Cancelar',
	},

	shareModal: {
		SHARE_DESIGN: 'Compartir tu diseño',
		SHARE_TEMPLATE: 'Compartir tu plantilla',
		SEND_LINK_TITLE:
			'Comparte tu diseño por correo electrónico, redes sociales, tu sitio web y más.',
		SEND_LINK_TITLE_TEMPLATE: '¿Estás listo para compartir tu plantilla?',
		SEND_LINK_DESCRIPTION: 'Solo copia y pega el enlace a continuación.',
		COPY_LINK: 'Copiar enlace',
		COPY_LINK_TEMPLATE: 'Copiar enlace a tu plantilla',
		COPY_LINK_DESCRIPTION:
			'Cualquier persona con este enlace puede ver tu diseño.',
		COPY_LINK_DESCRIPTION_TEMPLATE:
			'Esto significa que cualquier persona con tu enlace compartible puede ver tu diseño.',
		MAKE_PRIVATE: '¿Hacerlo privado?',
		MAKE_PRIVATE_DESCRIPTION:
			'Si haces tu diseño privado, las personas con el enlace no podrán verlo.',
		OPEN_LINK: 'Abrir enlace',
	},

	downloadBar: {
		DOWNLOAD: 'Descargar PDF',
	},

	productDataControl: {
		MANAGE_PRODUCT: 'Administrar producto',
		PRODUCT_DATA: 'Datos del producto',
		PAGE: 'Página',
		CANVAS: 'Página',
		SIDEBAR: 'Barra lateral',
		DATA: 'Datos',
		TEMPLATE: 'Plantilla',
		UPDATE_ALL: 'Actualizar Todo',
		COLORS: 'Colores',
		COLOR: 'Color',
		SIZES: 'Tamaños',
		NO_IMAGES: 'Sin imágenes',
		NO_IMAGES_SELECTED: 'No hay imágenes seleccionadas',
		SEE_ALL: 'Ver todo',
		NO_COLORS_SELECTED: 'No hay colores seleccionados',
		NO_SIZES_SELECTED: 'No hay tamaños seleccionados',
		UPLOADS: 'Cargas',
		DEFAULT: 'Predeterminado',
		BASIC: 'Básico',
		QUANTITY: 'Cantidad',
		PRICE: 'Precio',
		ACTIONS: 'Acciones',
		PRICING_CODE: 'Código de precio',
		PRICING_TABLE: 'Tabla de precios',
		SINGLE_PRICE: 'Precio único',
		MOQ_IS_AUTOMATICALLY_POPULATED:
			'El MOQ se completa automáticamente según los datos de precios.',
		ALLOW_REPLACING_THIS_PRODUCT: '¿Permitir reemplazar este producto?',
		ALLOW_EDITING_PRODUCT_INFORMATION:
			'¿Permitir editar la información del producto?',
		YES_THIS_PRODUCT_CAN_BE_REPLACED: 'Sí, este producto puede ser reemplazado.',
		YES_THE_PRODUCT_INFORMATION_CAN_BE_EDITED:
			'Sí, la información del producto puede ser editada.',
		EDITING_DISABLED_FOR_THIS_PRODUCT:
			'La edición ha sido deshabilitada para este producto por el creador de la plantilla.',
		PLEASE_PROVIDE_A_VALID_PRICE: 'Por favor proporciona un precio válido',
		OTHER_FIELDS: 'Otros campos',
		HERE_YOU_CAN_EDIT_THE_PRODUCT_DATA:
			'Aquí puedes editar los datos del producto para este campo',
		NO_MORE_OPTIONS_TO_SELECT: 'No hay más opciones para seleccionar',
		VIRTUALS: 'Virtuales',
		YOUR_LOGO: 'Tu logo',
		PICK_IMPRINT_AREA: 'Seleccionar área de impresión',
		CREATE: 'Crear',
		YOUR_VIRTUALS: 'Tus virtuales',
		NO_VIRTUALS_ENABLED: 'No hay virtuales habilitados',
		NO_VIRTUALS_ANYMORE: 'Ya no hay virtuales',
		NO_VIRTUALS_CREATED: 'No hay virtuales creados',
		NO_IMPRINT_AREAS: 'No hay áreas de impresión',
		SELECTED_IMAGE: 'Imagen seleccionada',
		UNSELECTED_IMAGE: 'Imagen no seleccionada',
		VS: 'VS',
		ADD_LOGO: 'Agregar logo',
		ALL_IMAGES: 'Todas las imágenes',
		MY_UPLOADS: 'Mis cargas',
		MY_VIRTUALS: 'Mis virtuales',
		VIRTUAL_SAMPLE_ENABLED: 'Muestra virtual habilitada',
		PRODUCT_IMAGES: 'Imágenes del producto',
		ALL_COLORS: 'Todos los colores',
		CREATE_VIRTUAL_SAMPLES: 'Crear Muestras Virtuales',
		UNNAMED: 'Sin nombre',
		CALCULATED_PRICING: 'Precio calculado',
		DISCOUNT: 'Descuento',
		MARK_UP: 'Margen',
		ENABLED: 'Habilitado',
		DISABLED: 'Deshabilitado',
		ADD_ALL: 'Agregar todo',
		REMOVE_ALL: 'Eliminar todo',
		USE_LIST_PRICING: 'Usar lista de precios',
		ADD_MY_OWN_PRICING: 'Agregar mi propio precio',
		NO_PRICING: 'Sin precio',
		YOU_HAVE_SELECTED_TO_REMOVE_ANY_PRICING:
			'Has seleccionado eliminar cualquier precio de este producto.',
		DO_YOU_WANT_TO_ADD_PRICE_PLACEHOLDER:
			'¿Quieres agregar un marcador de precio?',
		NO_LEAVE_IT_BLANK: 'No, déjalo en blanco',
		YES_ADD_CALL_FOR_PRICING: 'Sí, agregar "Consultar precio"',
		PRICE_CODE: 'Código de precio',
		INCLUDE_PRICE_CODE: 'Incluir código de precio',
		ADD_A_MARK_UP_DOWN: 'Agregar un margen/descuento',
		APPLY: 'Aplicar',
		REMOVE: 'Eliminar',
		THIS_PRODUCT_USES_THE_GLOBAL_MARKUP: 'Este producto usa el margen global',
		ENABLE_A_PRODUCT_SPECIFIC_MARKUP:
			'Habilitar un margen específico del producto',
		MARK_UP_DOWN_PRICES: 'Margen/descuento de precios',
		EDIT: 'Editar',
		SHOW_IN_SMART_SIDEBAR: 'Mostrar en la barra lateral inteligente',
		APPLY_TO_ALL_ON_PAGE: 'Aplicar a todos en la página',
		APPLY_TO_PAGES_SAME_LAYOUT: 'Aplicar a páginas usando el mismo diseño',
		UPDATE_ALL_SIDEBARS: 'Actualizar todas las barras laterales',
		NO_DATA_CAN_BE_EDITED: 'No se pueden editar datos',
		DATA_CONTROLS: {
			PRODUCT_ID: 'ID del producto',
			PRODUCT_IMAGE: 'Fotos del producto',
			PRODUCT_NAME: 'Nombre del producto',
			DESCRIPTION: 'Descripción',
			MATERIALS: 'Materiales',
			COLORS: 'Colores',
			SWATCHES: 'Muestras',
			SIZES: 'Tamaños',
			PRICING: 'Precios',
			MOQ: 'MOQ',
			WEBSITE: 'Llamado a la acción',
			PRODUCT_IMAGE_TWO: 'Segunda imagen del producto',
			PRICE_CODE: 'Código de precio',
		},
	},

	common: {
		// Actions
		SAVE: 'Guardar',
		CANCEL: 'Cancelar',
		DELETE: 'Eliminar',
		EDIT: 'Editar',
		ADD: 'Agregar',
		CLOSE: 'Cerrar',
		CONFIRM: 'Confirmar',
		BACK: 'Atrás',
		CONTINUE: 'Continuar',
		SKIP: 'Saltar',

		// States
		LOADING: 'Cargando...',
		WELCOME: 'Bienvenido a Smart Layouts',
		ERROR: 'Error',
		SUCCESS: 'Éxito',
		WARNING: 'Advertencia',

		// Validation
		VALUE_MUST_BE_MAX_64_CHARS: 'El valor debe tener un máximo de 64 caracteres',
		FIELD_REQUIRED_VALID_INPUT:
			'Este campo es obligatorio, por favor ingrese un valor válido',

		// Error Messages
		UNABLE_TO_LOAD_APP_DETAILS:
			'No se pudieron cargar los detalles de la aplicación. Por favor intenta de nuevo o contacta soporte',
		PLEASE_TRY_AGAIN_OR_CONTACT_SUPPORT:
			'Por favor intente nuevamente o contacte a soporte.',
		TRY_REFRESHING_OR_CONTACT_SUPPORT: 'Intente actualizar o contacte a soporte.',
		SOMETHING_WENT_WRONG: 'Algo salió mal',
		FORBIDDEN_PROJECT_NOT_ASSOCIATED:
			'Proyecto prohibido no asociado con la empresa',
		PROJECT_LOCKED_RETRY_LATER:
			'Actualmente el proyecto está bloqueado, por favor reintente más tarde.',
		PENDING_UPDATE_DETECTED:
			'Actualización pendiente detectada, aplicando ahora. Por favor espere...',
	},

	errors: {
		// Auth Errors
		UNABLE_TO_REFRESH_TOKEN: 'No se pudo actualizar el token',
		UNABLE_TO_VALIDATE_EMAIL: 'No se pudo validar el correo electrónico',
		UNABLE_TO_LOGIN: 'No se pudo iniciar sesión (verifica tus credenciales)',
		UNABLE_TO_LOGOUT: 'No se pudo cerrar sesión',
		UNABLE_TO_REQUEST_PASSWORD_RESET:
			'No se pudo solicitar el restablecimiento de contraseña',
		UNABLE_TO_RESET_PASSWORD: 'No se pudo restablecer la contraseña',
		UNABLE_TO_VERIFY_RESET_PASSWORD:
			'No se pudo verificar la solicitud de restablecimiento de contraseña',
		UNABLE_TO_SIGN_UP: 'No se pudo registrar',
		UNABLE_TO_RESEND_SIGNUP_VERIFICATION:
			'No se pudo reenviar la verificación de registro',
		UNABLE_TO_VERIFY_SIGNUP: 'No se pudo verificar el registro',

		// Product Errors
		UNABLE_TO_LOAD_PRODUCTS: 'No se pudieron cargar los productos',
		UNABLE_TO_LOAD_PRODUCT_DETAILS:
			'No se pudieron cargar los detalles del producto',
		UNABLE_TO_LOAD_PRODUCT_ATTRIBUTES:
			'No se pudieron cargar los atributos del producto',

		// Asset Errors
		UNABLE_TO_LOAD_ASSET: 'No se pudo cargar el recurso',
		UNABLE_TO_PREPARE_UPLOAD: 'No se pudo preparar la carga',
		UNABLE_TO_LOAD_ASSETS: 'No se pudieron cargar los recursos',
		UNABLE_TO_UPLOAD_ASSET: 'No se pudo subir el recurso',
		UNABLE_TO_LOAD_UNWRAPPED_S3_URL: 'No se pudo cargar la URL S3 desenvuelta',
		UNABLE_TO_LOAD_IMPRINT_AREAS: 'No se pudieron cargar las áreas de impresión',
		UNABLE_TO_LOAD_ASSET_SETTINGS:
			'No se pudieron cargar las configuraciones del recurso',
		UNABLE_TO_LOAD_ASSET_VARIATIONS:
			'No se pudieron cargar las variaciones del recurso',
		UNABLE_TO_CREATE_ASSET_VARIATION: 'No se pudo crear la variación del recurso',
		UNABLE_TO_LOAD_ASSET_VARIATION: 'No se pudo cargar la variación del recurso',

		// Virtual Sample Errors
		UNABLE_TO_LOAD_VIRTUAL_SAMPLE: 'No se pudo cargar la muestra virtual',
		UNABLE_TO_LOAD_VIRTUAL_SAMPLES:
			'No se pudieron cargar las muestras virtuales',
		UNABLE_TO_CREATE_VIRTUAL_SAMPLE: 'No se pudo crear la muestra virtual',
		UNABLE_TO_PUBLISH_VIRTUAL_SAMPLE: 'No se pudo publicar la muestra virtual',
		UNABLE_TO_EDIT_VIRTUAL_SAMPLE: 'No se pudo editar la muestra virtual',
		UNABLE_TO_CREATE_VIRTUAL_SAMPLES:
			'No se pudieron crear las muestras virtuales',
		UNABLE_TO_CREATE_VIRTUAL_SAMPLE_IMAGE:
			'No se pudo crear la imagen de la muestra virtual',
		UNABLE_TO_CREATE_VIRTUAL_SAMPLE_LAYER:
			'No se pudo crear la capa de la muestra virtual',
		UNABLE_TO_UPDATE_VIRTUAL_SAMPLE_LAYER:
			'No se pudo actualizar la capa de la muestra virtual',
		UNABLE_TO_DELETE_VIRTUAL_SAMPLE_LAYER:
			'No se pudo eliminar la capa de la muestra virtual',
	},

	productFilters: {
		TYPE_AND_SEARCH: 'Escribir y buscar',
		NEW_ARRIVALS: 'Nuevos Productos',
		PRICE: 'Precio',
		CATEGORY: 'Categoría',
		BRAND: 'Marca',
		TYPE: 'Tipo',
		DEPARTMENT: 'Departamento',
		LICENSE: 'Licencia',
		TREND: 'Tendencia',
		SEASON: 'Temporada',
		ADD_FILTER: 'Agregar filtro',
	},

	templateSettings: {
		WATCH_TUTORIAL: 'Ver tutorial',
		LET_OTHERS_DUPLICATE_AND_EDIT_YOUR_DESIGN:
			'Convierte tu diseño en una plantilla editable.',
		YOU_CAN_SHARE_YOUR_DESIGN:
			'Compartir como plantilla permite que otros dupliquen y personalicen tu diseño como propio.',
		DO_YOU_WANT_TO_MAKE_THIS_DESIGN_A_TEMPLATE:
			'¿Quieres convertir este diseño en una plantilla compartible?',
		A_TEMPLATE_ALLOWS_OTHERS_TO_DUPLICATE_YOUR_DESIGN:
			'Una plantilla permite que otros dupliquen tu diseño y lo usen para sí mismos.',
	},

	shareTemplateModal: {
		SEND_LINK_DESCRIPTION_TEMPLATE:
			'Compartir como plantilla permite que otros dupliquen y personalicen tu diseño como propio.',
		ADD_TEMPLATE_PORTAL_TITLE: 'Agregar plantilla a la biblioteca',
		ADD_TEMPLATE_PORTAL_DESCRIPTION:
			'Cuando esté habilitado, esta plantilla estará disponible en tu biblioteca de plantillas de ZOOMstudio para distribuidores y tu equipo.',
		SET_TEMPLATES_PERMISSIONS_TITLE: 'Configurar permisos de la plantilla',
		SET_TEMPLATES_PERMISSIONS_DESCRIPTION:
			'Elige quién puede ver y usar esta plantilla.',
		ONLY_YOU: 'Solo tú',
		YOUR_TEAM: 'Tu equipo',
		PUBLIC: 'Público',
	},

	virtualsTab: {
		WATCH_VIDEO: 'Ver video',
		TITLE: 'Crear Muestras Virtuales',
		SUBTITLE:
			'Sube un logo para aplicarlo automáticamente a los productos en este diseño.',
		SELECT_YOUR_ARTWORK: 'Seleccionar logo',
		USE_VIRTUALS_BY_DEFAULT: 'Aplicar logos a productos',
	},

	virtualsProcessor: {
		FETCHING_PAGES: 'Recopilando páginas',
		FETCHING_IMPRINT_AREAS: 'Buscando áreas de impresión',
		QUEUE: 'En cola para procesamiento',
		PROCESSING_VIRTUAL: 'Procesando virtual',
		IN_PROGRESS: (count: number, total: number) =>
			`Procesando ${count} de ${total} virtuales`,
	},

	assetPicker: {
		FILE_TOO_LARGE: 'Lo sentimos, tu archivo es demasiado grande',
		FILE_TOO_SMALL: 'Lo sentimos, tu archivo es demasiado pequeño',
		TOO_MANY_FILES: 'Lo sentimos, intentaste subir demasiados archivos',
		INVALID_FILE_TYPE: 'Lo sentimos, solo admitimos JPG/JPEG/PNG',
		SOMETHING_WENT_WRONG: 'Algo salió mal al seleccionar el recurso',
		UPDATE_ERROR:
			'Ocurrió un error al actualizar. Por favor actualiza la página.',
		ASSET_LIBRARY: 'Biblioteca de recursos',
		UPLOAD_YOUR_FILES: 'Suba sus archivos',
		UPLOAD_COMPLETE: 'Carga completa',
		SELECT_FILE_FROM_COMPUTER: 'Subir',
		SHARED_LIBRARY_EMPTY: 'Aún no hay recursos compartidos, vuelva más tarde',
		EXISTING_FILES: (quantity: number) =>
			`${quantity} archivos existen en su biblioteca`,
		UPLOAD_MESSAGE: 'Es hora de subir su primera imagen a ZOOMcatalog',
		TIP: 'Consejo: También puede arrastrar y soltar',
		SEARCH_IMAGES: 'Buscar imágenes',
		FILTER: 'Filtrar',
		FREE_TO_USE: 'Imágenes de uso libre de',
		FORMATS_JPG_OR_PNG_ONLY: 'Solo formatos JPG o PNG.',
		ACTION_LABEL: 'Acción',
		AUTHOR_LABEL: 'Autor',
		CATEGORIES: {
			UPLOADED_IMAGES: 'Sus cargas',
			UNSPLASH: 'Unsplash',
			SHARED_LIBRARY: 'Biblioteca compartida',
		},
		ASSETS: {
			TYPE: 'Tipo',
			COVER_IMAGE: 'Imagen de portada',
			LOGOS: 'Logos',
			OTHER: 'Otro',
		},
		PERMISSIONS_ONLY_YOU: 'Solo usted',
		PERMISSIONS_YOUR_TEAM: 'Su equipo',
		PERMISSION_PUBLIC: 'Público',
		VIRTUALS: 'Muestras virtuales',
		SHORTENED_VIRTUALS: 'VS',
		EDIT: 'Editar',
		APP_NAME: 'Storybook',
		NO_RESULTS: 'No se encontraron resultados',
		TRY_SOMETHING_DIFFERENT:
			'No pudimos encontrar imágenes que coincidan con su búsqueda. Pruebe algo diferente',
		SELECT: 'Seleccionar',
		EDIT_ASSET: 'Editar recurso',
		BY: 'Por',
		REMOVE_BACKGROUND: 'Eliminar color de fondo',
		CHANGE_COLOR: 'Cambiar color',
		MORE_VERSIONS_IMAGE: 'Más versiones de esta imagen',
	},

	// Asset package translations
	assets: {
		IMAGE_EDITOR: 'Editor de imágenes',
		SAVE: 'Guardar',
		REMOVE: 'Eliminar',
		ALL: 'Todos',
		CONTINUOUS: 'Continuo',
		REMOVE_BACKGROUND: 'Eliminar color de fondo',
		SELECT_COLOR: 'Seleccionar color',
		CHANGE_COLOR: 'Cambiar color',
		EDIT_ASSET: 'Editar logo',
		REPLACE_ASSET: 'Reemplazar logo',
		ADD_IMAGE_TEXT: 'Agregar logo',
		ADD_IMAGE: 'Agregar un logo',
		EDIT_IMAGE: 'Editar logo',
		REMOVE_IMAGE: 'Eliminar logo',
		REPLACE_IMAGE: 'Reemplazar logo',
		ADD_TEXT: 'Agregar un texto',
		EDIT_TEXT: 'Editar texto',
		REMOVE_TEXT: 'Eliminar texto',
		TEXT_PLACEHOLDER: 'Ingrese su texto ...',
		FONT_FAMILY: 'Familia de fuentes',
		IMPRINT_AREAS: 'Áreas de impresión',
		LOAD_DESIGN_ERROR:
			'Error al cargar el diseño. Por favor, inténtelo de nuevo más tarde.',
	},

	virtualSampleEditor: {
		REPOSITION_LOGO: 'Reposicionar logo',
		SAVE_CHANGES: 'Guardar cambios',
		DISCLAIMER_TEXT:
			'Advertencia: Las muestras virtuales son solo con fines ilustrativos y no garantizan la apariencia del producto impreso final. Las áreas de impresión del producto pueden tener restricciones en la ubicación, color y tamaño del logo.',
	},

	logoEditor: {
		EDIT_LOGO: 'Editar logo',
	},

	productImageField: {
		IMAGES_AND_VIRTUAL_SAMPLES: 'Imágenes y Muestras Virtuales',
		IMAGE_SINGULAR: 'Imagen',
		IMAGES_PLURAL: 'Imágenes',
	},

	noPermission: {
		TITLE: 'No tienes permisos para editar este diseño',
		DESCRIPTION: 'Puedes crear un nuevo diseño o abrir uno de tu biblioteca',
		CTA: 'Crear nuevo diseño',
	},
}
